import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  useTheme,
  Divider,
} from "@mui/material";
import ButtonComp from "./Button";
import FileUpload from "./FileUpload";
import { useState } from "react";
import PayoutSwitch from "./PayoutSwitch";

type SubmitPopup = {
  open: boolean;
  onClose: () => void;
  onSubmit: any;
  bankingInfoData: any;
  getFinanceFile: (value: any) => void;
  fileName: any;
  serviceFileName: any;
  claimBenefitInfo: any;
  getServiceFile: (value: any) => void;
};

const FinanceSubmitPopup = ({
  open,
  onClose,
  onSubmit,
  bankingInfoData,
  getFinanceFile,
  fileName,
  serviceFileName,
  claimBenefitInfo,
  getServiceFile,
}: SubmitPopup) => {
  const theme = useTheme();
  const [fileAttached, setFileAttached] = useState(false);
  const [serviceFileAttached, setServiceFileAttached] = useState(false);
  const [selectedTab, setSelectedTab] = useState("cash");

  const parseAmount = (value: any) => parseFloat(value || "0") || 0;

  // Filter and sum cash values
  const totalCash = claimBenefitInfo
    ?.map((item: any) => parseAmount(item.cash))
    .reduce((acc: any, curr: any) => acc + curr, 0);

  // Filter and sum service values
  const totalService = claimBenefitInfo
    ?.map((item: any) => parseAmount(item.service))
    .reduce((acc: any, curr: any) => acc + curr, 0);

  return (
    <>
      {totalCash > 0 && totalService > 0 ? (
        <Dialog
          maxWidth={false}
          open={open}
          onClose={onClose}
          aria-labelledby="finance-dialog-title"
          aria-describedby="finance-dialog-description"
          slotProps={{
            paper: {
              sx: {
                borderRadius: 4,
                overflow: "hidden",
                width: { xs: "95%", sm: 800 },
              },
            },
          }}
        >
          {/* Header */}
          <Box
            sx={{
              backgroundColor: "#F26522",
              height: 60,
              display: "flex",
              alignItems: "center",
              px: 2,
              position: "relative",
            }}
          ></Box>

          {/* Content */}
          <DialogContent
            sx={{
              backgroundColor: theme.palette.background.default,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <PayoutSwitch
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
            />
            {/* Banking Details Card */}
            {selectedTab === "cash" && (
              <Box
                sx={{
                  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
                  borderRadius: 2,
                  backgroundColor: "#fff",
                  p: 3,
                  mt: 2,
                }}
              >
                <Typography
                  variant="h6"
                  color="#EC6E00"
                  fontSize={25}
                  fontWeight={600}
                  textAlign="left"
                >
                  BANKING INFORMATION
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Box
                  display="grid"
                  gridTemplateColumns={{
                    xs: "1fr", // 1 column on extra-small screens
                    sm: "repeat(2, 1fr)", // 2 columns on small screens
                    md: "repeat(3, 1fr)", // 3 columns on medium and above
                  }}
                  rowGap={2}
                  columnGap={2}
                >
                  <LabelValue
                    label="Bank name"
                    value={bankingInfoData?.bank_name}
                  />
                  <LabelValue
                    label="Account name"
                    value={bankingInfoData?.account_name}
                  />
                  <LabelValue
                    label="Account type"
                    value={bankingInfoData?.account_type}
                  />

                  <LabelValue
                    label="Branch code"
                    value={bankingInfoData?.branch_code}
                  />
                  <LabelValue
                    label="Account number"
                    value={bankingInfoData?.account_number}
                  />
                  <LabelValue
                    label="Verified"
                    value={bankingInfoData?.is_verified ? "Yes" : "No"}
                  />
                </Box>

                <Typography fontWeight={600} mt={2} fontSize={20}>
                  Amount to be paid
                </Typography>
                <Typography fontSize={20}>
                  {/* $10.00 */}R{totalCash}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    mt: 2,
                  }}
                >
                  <Typography variant="body1" fontWeight={600} fontSize={18}>
                    Attach payment reciept
                  </Typography>
                  <FileUpload
                    inputId="finance-file-upload"
                    fileName={fileName?.name ?? "No file chosen"}
                    onChange={async (e) => {
                      const selectedFile = e.target.files?.[0];
                      getFinanceFile(selectedFile);
                      setFileAttached(!!selectedFile);
                    }}
                  />
                </Box>
              </Box>
            )}

            {selectedTab === "service" && (
              <Box
                sx={{
                  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
                  borderRadius: 2,
                  backgroundColor: "#fff",
                  p: 3,
                  mt: 2,
                }}
              >
                <Typography fontWeight={600} mt={2} fontSize={20}>
                  Amount to be paid
                </Typography>
                <Typography fontSize={20}>
                  {/* $10.00 */}R{totalService}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    mt: 2,
                  }}
                >
                  <Typography variant="body1" fontWeight={600} fontSize={18}>
                    Attach payment reciept
                  </Typography>
                  <FileUpload
                    inputId="service-file-upload"
                    fileName={serviceFileName?.name ?? "No file chosen"}
                    onChange={async (e) => {
                      const selectedFile = e.target.files?.[0];
                      getServiceFile(selectedFile);
                      setServiceFileAttached(!!selectedFile);
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* Action Buttons */}
            <Box
              display="flex"
              gap={2}
              mt={2}
              justifyContent="flex-start"
              flexDirection={{ xs: "column", sm: "row" }}
            >
              <ButtonComp
                variant="success"
                height={40}
                width={190}
                onClick={() => {
                  onClose();
                  onSubmit("Paid");
                }}
                disabled={!(fileAttached && serviceFileAttached)}
              >
                PAY
              </ButtonComp>
              <ButtonComp
                variant="error"
                height={40}
                width={190}
                onClick={() => {
                  onClose();
                  setSelectedTab("cash");
                }}
              >
                CANCEL
              </ButtonComp>
            </Box>
          </DialogContent>
        </Dialog>
      ) : totalCash > 0 ? (
        <Dialog
          maxWidth={false}
          open={open}
          onClose={onClose}
          aria-labelledby="finance-dialog-title"
          aria-describedby="finance-dialog-description"
          slotProps={{
            paper: {
              sx: {
                borderRadius: 4,
                overflow: "hidden",
                width: { xs: "95%", sm: 800 },
              },
            },
          }}
        >
          {/* Header */}
          <Box
            sx={{
              backgroundColor: "#F26522",
              height: 60,
              display: "flex",
              alignItems: "center",
              px: 2,
              position: "relative",
            }}
          ></Box>

          {/* Content */}
          <DialogContent
            sx={{
              backgroundColor: theme.palette.background.default,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {/* Banking Details Card */}
            <Box
              sx={{
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
                borderRadius: 2,
                backgroundColor: "#fff",
                p: 3,
              }}
            >
              <Typography
                variant="h6"
                color="#EC6E00"
                fontSize={25}
                fontWeight={600}
                textAlign="left"
              >
                BANKING INFORMATION
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box
                display="grid"
                gridTemplateColumns="repeat(3, 1fr)"
                rowGap={2}
                columnGap={2}
              >
                <LabelValue
                  label="Bank name"
                  value={bankingInfoData?.bank_name}
                />
                <LabelValue
                  label="Account name"
                  value={bankingInfoData?.account_name}
                />
                <LabelValue
                  label="Account type"
                  value={bankingInfoData?.account_type}
                />

                <LabelValue
                  label="Branch code"
                  value={bankingInfoData?.branch_code}
                />
                <LabelValue
                  label="Account number"
                  value={bankingInfoData?.account_number}
                />
                <LabelValue
                  label="Verified"
                  value={bankingInfoData?.is_verified ? "Yes" : "No"}
                />
              </Box>

              <Typography fontWeight={600} mt={2} fontSize={20}>
                Amount to be paid
              </Typography>
              <Typography fontSize={20}>
                {/* $10.00 */}R{totalCash}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="body1" fontWeight={600} fontSize={18}>
                Attach payment reciept
              </Typography>
              <FileUpload
                inputId="finance-file-upload"
                fileName={fileName?.name ?? "No file chosen"}
                onChange={async (e) => {
                  const selectedFile = e.target.files?.[0];
                  getFinanceFile(selectedFile);
                  setFileAttached(!!selectedFile);
                }}
              />
            </Box>

            {/* Action Buttons */}
            <Box display="flex" gap={2} mt={2} justifyContent="flex-start">
              <ButtonComp
                variant="success"
                height={40}
                width={190}
                onClick={() => {
                  onClose();
                  onSubmit("Paid");
                }}
                disabled={!fileAttached}
              >
                PAY
              </ButtonComp>
              <ButtonComp
                variant="error"
                height={40}
                width={190}
                onClick={() => onClose()}
              >
                CANCEL
              </ButtonComp>
            </Box>
          </DialogContent>
        </Dialog>
      ) : totalService > 0 ? (
        <>
          <Dialog
            maxWidth={false}
            open={open}
            onClose={onClose}
            aria-labelledby="finance-dialog-title"
            aria-describedby="finance-dialog-description"
            slotProps={{
              paper: {
                sx: {
                  borderRadius: 4,
                  overflow: "hidden",
                  width: { xs: "95%", sm: 800 },
                },
              },
            }}
          >
            {/* Header */}
            <Box
              sx={{
                backgroundColor: "#F26522",
                height: 60,
                display: "flex",
                alignItems: "center",
                px: 2,
                position: "relative",
              }}
            ></Box>

            {/* Content */}
            <DialogContent
              sx={{
                backgroundColor: theme.palette.background.default,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {/* Banking Details Card */}
              <Box
                sx={{
                  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
                  borderRadius: 2,
                  backgroundColor: "#fff",
                  p: 3,
                }}
              >
                <Typography fontWeight={600} mt={2} fontSize={20}>
                  Amount to be paid
                </Typography>
                <Typography fontSize={20}>
                  {/* $10.00 */}R{totalService}
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  mt={3}
                >
                  <Typography variant="body1" fontWeight={600} fontSize={18}>
                    Attach payment reciept
                  </Typography>
                  <FileUpload
                    inputId="finance-file-upload"
                    fileName={serviceFileName?.name ?? "No file chosen"}
                    onChange={async (e) => {
                      const selectedFile = e.target.files?.[0];
                      getServiceFile(selectedFile);
                      setFileAttached(!!selectedFile);
                    }}
                  />
                </Box>
              </Box>

              <Box display="flex" gap={2} mt={2} justifyContent="flex-start">
                <ButtonComp
                  variant="success"
                  height={40}
                  width={190}
                  onClick={() => {
                    onClose();
                    onSubmit("Paid");
                  }}
                  disabled={!fileAttached}
                >
                  PAY
                </ButtonComp>
                <ButtonComp
                  variant="error"
                  height={40}
                  width={190}
                  onClick={() => onClose()}
                >
                  CANCEL
                </ButtonComp>
              </Box>
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <Dialog
          maxWidth={false}
          open={open}
          onClose={onClose}
          aria-labelledby="finance-dialog-title"
          aria-describedby="finance-dialog-description"
          slotProps={{
            paper: {
              sx: {
                borderRadius: 4,
                overflow: "hidden",
                width: { xs: "95%", sm: 800 },
              },
            },
          }}
        >
          {/* Header */}
          <Box
            sx={{
              backgroundColor: "#F26522",
              height: 60,
              display: "flex",
              alignItems: "center",
              px: 2,
              position: "relative",
            }}
          ></Box>

          {/* Content */}
          <DialogContent
            sx={{
              backgroundColor: theme.palette.background.default,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {/* Banking Details Card */}
            <Box
              sx={{
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
                borderRadius: 2,
                backgroundColor: "#fff",
                p: 3,
              }}
            >
              <Typography
                variant="h6"
                color="#EC6E00"
                fontSize={25}
                fontWeight={600}
                textAlign="left"
              >
                BANKING INFORMATION
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box
                display="grid"
                gridTemplateColumns="repeat(3, 1fr)"
                rowGap={2}
                columnGap={2}
              >
                <LabelValue
                  label="Bank name"
                  value={bankingInfoData?.bank_name}
                />
                <LabelValue
                  label="Account name"
                  value={bankingInfoData?.account_name}
                />
                <LabelValue
                  label="Account type"
                  value={bankingInfoData?.account_type}
                />

                <LabelValue
                  label="Branch code"
                  value={bankingInfoData?.branch_code}
                />
                <LabelValue
                  label="Account number"
                  value={bankingInfoData?.account_number}
                />
                <LabelValue
                  label="Verified"
                  value={bankingInfoData?.is_verified ? "Yes" : "No"}
                />
              </Box>

              <Typography fontWeight={600} mt={2} fontSize={20}>
                Amount to be paid
              </Typography>
              <Typography fontSize={20}>
                {/* $10.00 */}
                {bankingInfoData?.cashback_amount ?? "0.00"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="body1" fontWeight={600} fontSize={18}>
                Attach payment reciept
              </Typography>
              <FileUpload
                inputId="finance-file-upload"
                fileName={fileName?.name ?? "No file chosen"}
                onChange={async (e) => {
                  const selectedFile = e.target.files?.[0];
                  getFinanceFile(selectedFile);
                  setFileAttached(!!selectedFile);
                }}
              />
            </Box>

            {/* Action Buttons */}
            <Box display="flex" gap={2} mt={2} justifyContent="flex-start">
              <ButtonComp
                variant="success"
                height={40}
                width={190}
                onClick={() => {
                  onClose();
                  onSubmit("Paid");
                }}
                disabled={!fileAttached}
              >
                PAY
              </ButtonComp>
              <ButtonComp
                variant="error"
                height={40}
                width={190}
                onClick={() => onClose()}
              >
                CANCEL
              </ButtonComp>
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

const LabelValue = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Typography fontWeight={600} fontSize={20}>
      {label}
    </Typography>
    <Typography fontSize={20}>{value}</Typography>
  </Box>
);

export default FinanceSubmitPopup;
